import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import './alertsProfile.css';
import { PageHeader } from '@/components/common/PageHeader';
import { Select2 } from '@/components/forms/Select2';
import { Select2Async } from '@/components/forms/Select2Async';
import { Select2Multi } from '@/components/forms/Select2Multi';
import { Switch } from '@/components/forms/Switch';
import { TimePicker } from '@/components/forms/TimePicker';
import { DateRangePicker } from '@/components/forms/DateRangePicker';
import { AsyncButton } from '@/components/common/AsyncButton';
import { ConfirmDialog } from '@/components/dialogs/ConfirmDialog';
import { useToast } from '@/components/common/toast';
import { useAuth } from '@/auth/AuthContext';
import { ApiError } from '@/api/client/abpError';
import { filterDataApi, filterDataPaged } from '@/api/services/filterData';
import { currencyApi } from '@/api/services/currency';
import { tsAlertTemplateLoaderApi } from '@/api/services/tsAlertTemplateLoader';
import {
  TsAlertProfileType,
  TsComparisonOperator,
  TsTradeSession,
  type CreateTraderSurveillanceProfileInput,
  type TraderSurveillanceProfileDto,
} from '@/api/types/traderSurveillance';
import type { TsAlertTemplateDto } from '@/api/types/tsTemplates';
import { useEnumOptions } from '@/features/common/enums/useEnumOptions';
import { ViewProfileModal } from './ViewProfileModal';
import { ProfileList } from './ProfileLists';
import { TemplateLoaderList } from './TemplateLoaderList';
import {
  useActiveProfiles,
  useCreateProfile,
  useDeletedProfiles,
  useDeleteProfile,
  useEnabledTemplates,
  usePriorities,
  useSetProfileEnabled,
} from './api';

// ---- static option lists ----
// Comparison operators are rendered as symbols and trade sessions carry GMT
// windows — those are UI presentation / logic, not plain enum display data.
const OP_OPTS = [
  { value: String(TsComparisonOperator.NotApplicable), label: 'N/A' },
  { value: String(TsComparisonOperator.GreaterThan), label: '>' },
  { value: String(TsComparisonOperator.SmallerThan), label: '<' },
  { value: String(TsComparisonOperator.EqualTo), label: '=' },
];
const SESSION_OPTS = [
  { value: String(TsTradeSession.NotApplicable), label: 'N/A' },
  { value: String(TsTradeSession.Sydney), label: 'Sydney (9PM - 6AM GMT)' },
  { value: String(TsTradeSession.Tokyo), label: 'Tokyo (12AM - 9AM GMT)' },
  { value: String(TsTradeSession.London), label: 'London (7AM - 4PM GMT)' },
  { value: String(TsTradeSession.NewYork), label: 'New York (12PM - 9PM GMT)' },
  { value: String(TsTradeSession.Custom), label: 'Custom' },
];
const SESSION_WINDOWS: Record<number, [string, string]> = {
  1: ['21:00:00', '05:59:59'],
  2: ['00:00:00', '08:59:59'],
  3: ['07:00:00', '15:59:59'],
  4: ['12:00:00', '20:59:59'],
};
function shiftTime(hms: string, offsetMin: number): string {
  const [h, m, s] = hms.split(':').map(Number);
  const total = (((h * 60 + m + offsetMin) % 1440) + 1440) % 1440;
  const hh = Math.floor(total / 60);
  const mm = total % 60;
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}
function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

interface FormState {
  profileTag: string;
  priority: string;
  historyDate: string;
  type: string;
  reason: string;
  countryId: string;
  countryLabel: string;
  mifidClassification: string;
  marInstruments: boolean;
  tradeTime: string;
  tradePoints: string;
  volumeOperator: string;
  volume: string;
  notionalOperator: string;
  notional: string;
  tradeSession: string;
  openTimeRaw: string;
  closeTimeRaw: string;
  profitThresholdsPerTrade: boolean;
  profitThresholdsAmountPerTrade: string;
  cumulativeProfitThresholds: boolean;
  cumulativeProfitThresholdsAmount: string;
  cumulativeProfitThresholdsFloating: boolean;
  equityOperator: string;
  equity: string;
  marginLevelOperator: string;
  marginLevel: string;
  overnightDays: string;
  // tenant-only independent filters (do not take part in the normalize cascade)
  group: string;
  accountCurrency: string;
  login: string;
  connector: string;
  uniqueId: string;
  byUniqueId: boolean;
  instruments: string[];
}

function emptyForm(): FormState {
  return {
    profileTag: '', priority: 'Low', historyDate: todayIso(), type: String(TsAlertProfileType.Deals),
    reason: '', countryId: '', countryLabel: '', mifidClassification: '', marInstruments: false,
    tradeTime: '', tradePoints: '', volumeOperator: '0', volume: '', notionalOperator: '0', notional: '',
    tradeSession: '0', openTimeRaw: '', closeTimeRaw: '',
    profitThresholdsPerTrade: false, profitThresholdsAmountPerTrade: '',
    cumulativeProfitThresholds: false, cumulativeProfitThresholdsAmount: '', cumulativeProfitThresholdsFloating: false,
    equityOperator: '0', equity: '', marginLevelOperator: '0', marginLevel: '', overnightDays: '',
    group: '', accountCurrency: '', login: '', connector: '', uniqueId: '', byUniqueId: false, instruments: [],
  };
}

type Dis = Partial<Record<string, boolean>>;

// Faithful port of the legacy ts-alert-profile-form enable/disable cascade
// (same as the host TsTemplatesPage normalize). Tenant-only fields are untouched here.
function normalize(input: FormState): { f: FormState; dis: Dis } {
  const f: FormState = { ...input };
  const dis: Dis = {};
  const t = Number(f.type);
  const today = todayIso();

  if (t === TsAlertProfileType.Deals) {
    f.tradeTime = ''; f.tradePoints = '';
    f.equityOperator = '0'; f.equity = '';
    f.marginLevelOperator = '0'; f.marginLevel = '';
    f.overnightDays = '';
    f.profitThresholdsPerTrade = false; f.profitThresholdsAmountPerTrade = '';
    f.cumulativeProfitThresholds = false; f.cumulativeProfitThresholdsAmount = ''; f.cumulativeProfitThresholdsFloating = false;
    dis.tradeTime = dis.tradePoints = dis.equity = dis.marginLevel = dis.overnightDays = dis.perTrade = dis.cumulative = true;
  } else if (t === TsAlertProfileType.ClosedTrades) {
    f.equityOperator = '0'; f.equity = '';
    f.marginLevelOperator = '0'; f.marginLevel = '';
    f.overnightDays = '';
    dis.equity = dis.marginLevel = dis.overnightDays = true;
  } else {
    f.tradeTime = ''; f.tradePoints = '';
    f.volumeOperator = '0'; f.volume = '';
    f.notionalOperator = '0'; f.notional = '';
    f.tradeSession = '0'; f.openTimeRaw = ''; f.closeTimeRaw = '';
    f.profitThresholdsPerTrade = false; f.profitThresholdsAmountPerTrade = '';
    f.cumulativeProfitThresholds = false; f.cumulativeProfitThresholdsAmount = ''; f.cumulativeProfitThresholdsFloating = false;
    dis.tradeTime = dis.tradePoints = dis.session = dis.perTrade = dis.cumulative = true;
  }

  const s = Number(f.tradeSession);
  if (s >= 1 && s <= 4) {
    const off = -new Date().getTimezoneOffset();
    const [o, c] = SESSION_WINDOWS[s];
    f.openTimeRaw = shiftTime(o, off);
    f.closeTimeRaw = shiftTime(c, off);
  } else if (s === 0) {
    f.openTimeRaw = ''; f.closeTimeRaw = '';
  }

  const cumulativeLock = f.cumulativeProfitThresholds && !dis.cumulative;
  if (cumulativeLock) {
    dis.perTrade = true; f.profitThresholdsPerTrade = false; f.profitThresholdsAmountPerTrade = '';
    f.tradeTime = ''; dis.tradeTime = true;
    f.tradePoints = ''; dis.tradePoints = true;
    f.reason = ''; dis.reason = true;
    f.openTimeRaw = ''; f.closeTimeRaw = '';
    f.overnightDays = ''; dis.overnightDays = true;
    f.equityOperator = '0'; f.equity = ''; dis.equity = true;
    f.marginLevelOperator = '0'; f.marginLevel = ''; dis.marginLevel = true;
  } else if (f.profitThresholdsPerTrade && !dis.perTrade) {
    dis.cumulative = true;
  }

  if (f.overnightDays !== '') {
    dis.equity = true; dis.marginLevel = true; dis.tradeTime = true; dis.tradePoints = true;
    f.equityOperator = '0'; f.equity = ''; f.marginLevelOperator = '0'; f.marginLevel = '';
    f.tradeTime = ''; f.tradePoints = '';
    dis.historyDate = true; f.historyDate = today;
  }

  if (!(t === TsAlertProfileType.ClosedTrades || t === TsAlertProfileType.Deals || cumulativeLock)) {
    const anyTrade =
      Number(f.volumeOperator) !== 0 || Number(f.notionalOperator) !== 0 ||
      f.tradePoints !== '' || f.tradeTime !== '' ||
      f.profitThresholdsPerTrade || f.cumulativeProfitThresholds ||
      f.openTimeRaw !== '' || f.closeTimeRaw !== '';
    if (anyTrade || f.overnightDays !== '') { dis.equity = true; dis.marginLevel = true; }
    const noUser = Number(f.marginLevelOperator) === 0 && Number(f.equityOperator) === 0;
    dis.overnightDays = !noUser;
    if (!noUser) { dis.historyDate = true; f.historyDate = today; }
  }

  const isAcct = t === TsAlertProfileType.AccountConditions;
  const overnightsSet = f.overnightDays !== '';
  const volumeOn = Number(f.volumeOperator) !== 0;
  const notionalOn = Number(f.notionalOperator) !== 0;
  if (cumulativeLock) {
    dis.volume = true; f.volumeOperator = '0'; f.volume = '';
  } else if (isAcct && !overnightsSet) {
    dis.volume = true; dis.notional = true;
  } else if (volumeOn && !notionalOn) {
    dis.notional = true; f.notionalOperator = '0'; f.notional = '';
  } else if (notionalOn && !volumeOn) {
    dis.volume = true; f.volumeOperator = '0'; f.volume = '';
  }

  // MAR Instruments ON → clear + disable Instruments.
  if (f.marInstruments) {
    dis.instruments = true;
    f.instruments = [];
  }

  return { f, dis };
}

export function AlertsProfilePage() {
  const toast = useToast();
  const { tenantId } = useAuth();

  const [form, setForm] = useState<FormState>(() => normalize(emptyForm()).f);
  const [viewId, setViewId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TraderSurveillanceProfileDto | null>(null);
  const [toggleTarget, setToggleTarget] = useState<TraderSurveillanceProfileDto | null>(null);

  const priorities = usePriorities();
  const { options: tsEnums } = useEnumOptions(['TsAlertProfileType', 'TsReason']);
  const typeOpts = useMemo(
    () => (tsEnums.TsAlertProfileType ?? []).map((o) => ({ value: String(o.value), label: o.label })),
    [tsEnums.TsAlertProfileType],
  );
  // Reason excludes NotApplicable (value -1), which isn't offered in the UI.
  const reasonOpts = useMemo(
    () => (tsEnums.TsReason ?? []).filter((o) => o.value !== -1).map((o) => ({ value: String(o.value), label: o.label })),
    [tsEnums.TsReason],
  );
  const mifid = useQuery({ queryKey: ['rd-filter', 'mifid'], queryFn: filterDataApi.getMifidClassifications, staleTime: 5 * 60_000 });
  const connectors = useQuery({ queryKey: ['ts-non-terminal-connectors'], queryFn: filterDataApi.getNonTerminalConnectors, staleTime: 5 * 60_000 });
  const instruments = useQuery({ queryKey: ['ts-instruments'], queryFn: filterDataApi.getInstruments, staleTime: 5 * 60_000 });
  const currencies = useQuery({
    queryKey: ['ts-currencies', tenantId],
    queryFn: () => currencyApi.getCurrenciesSelectList(tenantId as string),
    enabled: !!tenantId,
    staleTime: 5 * 60_000,
  });

  const active = useActiveProfiles();
  const deleted = useDeletedProfiles();
  const templates = useEnabledTemplates();

  const createMut = useCreateProfile();
  const setEnabledMut = useSetProfileEnabled();
  const deleteMut = useDeleteProfile();

  const dis = useMemo(() => normalize(form).dis, [form]);
  const apply = (partial: Partial<FormState>) => setForm((f) => normalize({ ...f, ...partial }).f);
  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => apply({ [k]: v } as Partial<FormState>);
  const onSession = (v: string | null) => apply({ tradeSession: v || '0' });
  const onPerTrade = (c: boolean) => apply(c ? { profitThresholdsPerTrade: true, cumulativeProfitThresholds: false } : { profitThresholdsPerTrade: false });
  const onCumulative = (c: boolean) => apply(c ? { cumulativeProfitThresholds: true, profitThresholdsPerTrade: false } : { cumulativeProfitThresholds: false });

  // By Unique Id toggle: ON → hide login-filters (clear login/connector/group/accCurrency), show Unique Id;
  // OFF → reverse + clear Unique Id.
  const onByUniqueId = (c: boolean) =>
    apply(c ? { byUniqueId: true, login: '', connector: '', group: '', accountCurrency: '' } : { byUniqueId: false, uniqueId: '' });

  // Guard against a non-array payload (e.g. an error/HTML body from a failed request)
  // so a bad currency response degrades to an empty list instead of crashing the page.
  const currencyOpts = useMemo(
    () => (Array.isArray(currencies.data) ? currencies.data : []).map((c) => ({ value: c.value, label: c.text })),
    [currencies.data],
  );
  const instrumentOpts = Array.isArray(instruments.data) ? instruments.data : [];

  const numOrNull = (v: string) => (v === '' ? null : Number(v));
  const opOrNull = (v: string) => (Number(v) === 0 ? null : Number(v));

  function buildDto(): CreateTraderSurveillanceProfileInput {
    const sessionTimes = Number(form.tradeSession) && !dis.session;
    let openTime: string | null = null;
    let closeTime: string | null = null;
    if (sessionTimes && form.openTimeRaw && form.closeTimeRaw) {
      openTime = `${form.historyDate}T${form.openTimeRaw}`;
      closeTime = `${form.historyDate}T${form.closeTimeRaw}`;
      if (closeTime <= openTime) {
        const d = new Date(`${form.historyDate}T00:00:00`);
        d.setDate(d.getDate() + 1);
        closeTime = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}T${form.closeTimeRaw}`;
      }
    }
    return {
      profileTag: form.profileTag.trim(),
      priority: form.priority || null,
      historyDate: form.historyDate || todayIso(),
      type: Number(form.type),
      reason: form.reason === '' ? null : Number(form.reason),
      countryId: form.countryId === '' ? null : Number(form.countryId),
      mifidClassification: form.mifidClassification === '' ? null : Number(form.mifidClassification),
      marInstruments: form.marInstruments,
      tradeTime: numOrNull(form.tradeTime),
      tradePoints: numOrNull(form.tradePoints),
      volumeOperator: opOrNull(form.volumeOperator),
      volume: Number(form.volumeOperator) === 0 ? null : numOrNull(form.volume),
      notionalOperator: opOrNull(form.notionalOperator),
      notional: Number(form.notionalOperator) === 0 ? null : numOrNull(form.notional),
      tradeSession: opOrNull(form.tradeSession),
      openTime,
      closeTime,
      profitThresholdsPerTrade: form.profitThresholdsPerTrade,
      profitThresholdsAmountPerTrade: form.profitThresholdsPerTrade ? numOrNull(form.profitThresholdsAmountPerTrade) : null,
      cumulativeProfitThresholds: form.cumulativeProfitThresholds,
      cumulativeProfitThresholdsAmount: form.cumulativeProfitThresholds ? numOrNull(form.cumulativeProfitThresholdsAmount) : null,
      cumulativeProfitThresholdsFloating: form.cumulativeProfitThresholds && form.cumulativeProfitThresholdsFloating,
      cumulativeProfitThresholdsPeriod: null,
      equityOperator: opOrNull(form.equityOperator),
      equity: Number(form.equityOperator) === 0 ? null : numOrNull(form.equity),
      marginLevelOperator: opOrNull(form.marginLevelOperator),
      marginLevel: Number(form.marginLevelOperator) === 0 ? null : numOrNull(form.marginLevel),
      overnightDays: numOrNull(form.overnightDays),
      reportingCurrency: true,
      byUniqueId: form.byUniqueId,
      instrument: form.marInstruments ? null : form.instruments.length ? form.instruments.join(',') : null,
      uniqueId: form.byUniqueId ? form.uniqueId || null : null,
      login: !form.byUniqueId && form.login !== '' ? Number(form.login) : null,
      connector: !form.byUniqueId && form.connector !== '' ? Number(form.connector) : null,
      group: !form.byUniqueId ? form.group || null : null,
      accountCurrency: !form.byUniqueId ? form.accountCurrency || null : null,
    };
  }

  function validate(): string | null {
    const tag = form.profileTag.trim();
    if (!tag) return 'Profile Tag is required.';
    if (/^\d+$/.test(tag)) return 'Profile Tag cannot be only numbers.';
    if (!dis.historyDate && !form.historyDate) return 'Historical Date is required.';
    if (Number(form.volumeOperator) !== 0 && !dis.volume && form.volume === '') return 'Volume amount is required.';
    if (Number(form.notionalOperator) !== 0 && !dis.notional && form.notional === '') return 'Notional amount is required.';
    return null;
  }

  const clearForm = () => setForm(normalize(emptyForm()).f);

  async function submit() {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    try {
      await createMut.mutateAsync(buildDto());
      toast.success('Profile added successfully!');
      clearForm();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Failed to save the profile.');
    }
  }

  // Load a template into the form (CREATE mode — templates carry no login/connector/group).
  function loadTemplate(t: TsAlertTemplateDto) {
    setForm(
      normalize({
        ...emptyForm(),
        profileTag: t.profileTag ?? '',
        priority: t.priority ?? 'Low',
        historyDate: (t.historyDate ?? todayIso()).substring(0, 10),
        type: String(t.type ?? TsAlertProfileType.Deals),
        reason: t.reason == null ? '' : String(t.reason),
        countryId: t.countryId == null ? '' : String(t.countryId),
        countryLabel: t.countryName ?? '',
        mifidClassification: t.mifidClassification == null ? '' : String(t.mifidClassification),
        marInstruments: t.marInstruments,
        tradeTime: t.tradeTime == null ? '' : String(t.tradeTime),
        tradePoints: t.tradePoints == null ? '' : String(t.tradePoints),
        volumeOperator: String(t.volumeOperator ?? 0),
        volume: t.volume == null ? '' : String(t.volume),
        notionalOperator: String(t.notionalOperator ?? 0),
        notional: t.notional == null ? '' : String(t.notional),
        tradeSession: String(t.tradeSession ?? 0),
        openTimeRaw: t.openTime ? t.openTime.substring(11, 19) : '',
        closeTimeRaw: t.closeTime ? t.closeTime.substring(11, 19) : '',
        profitThresholdsPerTrade: t.profitThresholdsPerTrade,
        profitThresholdsAmountPerTrade: t.profitThresholdsAmountPerTrade == null ? '' : String(t.profitThresholdsAmountPerTrade),
        cumulativeProfitThresholds: t.cumulativeProfitThresholds,
        cumulativeProfitThresholdsAmount: t.cumulativeProfitThresholdsAmount == null ? '' : String(t.cumulativeProfitThresholdsAmount),
        cumulativeProfitThresholdsFloating: t.cumulativeProfitThresholdsFloating,
        equityOperator: String(t.equityOperator ?? 0),
        equity: t.equity == null ? '' : String(t.equity),
        marginLevelOperator: String(t.marginLevelOperator ?? 0),
        marginLevel: t.marginLevel == null ? '' : String(t.marginLevel),
        overnightDays: t.overnightDays == null ? '' : String(t.overnightDays),
      }).f,
    );
  }

  async function useTemplate(id: number) {
    try {
      const t = await tsAlertTemplateLoaderApi.getTemplate(id);
      loadTemplate(t);
      toast.info('Template loaded. Review and click Create.');
    } catch {
      toast.error('Failed to load the template.');
    }
  }

  function confirmToggle() {
    if (!toggleTarget) return;
    const enabled = !toggleTarget.enabled;
    setEnabledMut.mutate(
      { id: toggleTarget.id, enabled },
      {
        onSuccess: () => {
          toast.success(enabled ? 'Profile enabled.' : 'Profile disabled.');
          setToggleTarget(null);
        },
        onError: (e) => {
          toast.error(e instanceof ApiError ? e.message : 'Failed to update the profile.');
          setToggleTarget(null);
        },
      },
    );
  }
  function confirmDelete() {
    if (!deleteTarget) return;
    deleteMut.mutate(deleteTarget.id, {
      onSuccess: () => {
        toast.success('Profile deleted.');
        setDeleteTarget(null);
      },
      onError: (e) => {
        toast.error(e instanceof ApiError ? e.message : 'Failed to delete the profile.');
        setDeleteTarget(null);
      },
    });
  }

  const sessionTimesVisible = Number(form.tradeSession) !== 0 && !dis.session;
  const typeNum = Number(form.type);
  const timeLabel = (which: 'open' | 'close'): string => {
    if (typeNum === TsAlertProfileType.ClosedTrades) return which === 'open' ? 'Open Time' : 'Close Time';
    return which === 'open' ? 'Start time' : 'End time';
  };

  return (
    <div className="alerts-profile-page">
      <PageHeader title="Alerts Profile" breadcrumb={['Home', 'Trader Surveillance', 'Alerts Profile']} />

      {/* Buttons bar */}
      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        <AsyncButton className="btn btn-primary btn-sm" icon="fa fa-plus" onClick={submit}>
          Create
        </AsyncButton>
        <button type="button" className="btn btn-link btn-sm" onClick={clearForm}>
          Clear All Filters
        </button>
        <div className="ms-auto d-flex align-items-center gap-3">
          <Switch label="Reporting Currency" checked disabled onChange={() => {}} />
        </div>
      </div>

      <div className="row g-2">
        {/* Left: authoring form */}
        <div className="col-lg-9">
          <Section title="Profile Info">
            <div className="row g-2">
              <div className="col-lg-4">
                <label className="form-label">Profile Tag</label>
                <input className="form-control" value={form.profileTag} onChange={(e) => set('profileTag', e.target.value)} />
              </div>
              <div className="col-lg-4">
                <label className="form-label">Historical Date</label>
                <DateRangePicker mode="single" value={form.historyDate} onChange={(v) => set('historyDate', v)} disabled={dis.historyDate} />
              </div>
              <Select2
                key="priority"
                className="col-lg-4"
                label="Priority"
                searchable={false}
                allowClear={false}
                value={form.priority}
                options={(priorities.data ?? []).map((p) => ({ value: p.value, label: p.text }))}
                onChange={(v) => set('priority', v || 'Low')}
              />
            </div>
          </Section>

          <Section title="Filters">
            <div className="row g-2 align-items-end">
              <Select2 key="reason" className="col-lg-4" label="Reason" searchable={false} allowClear placeholder="All" value={form.reason} options={reasonOpts} onChange={(v) => set('reason', v ?? '')} disabled={dis.reason} />
              <div className="col-lg-4">
                <label className="form-label">Country</label>
                <Select2Async placeholder="All" value={form.countryId || null} valueLabel={form.countryLabel || undefined} fetch={filterDataPaged.countries} onChange={(v) => set('countryId', v ?? '')} />
              </div>
              <Select2 key="mifid" className="col-lg-4" label="MiFID Classification" searchable={false} allowClear placeholder="All" value={form.mifidClassification} options={mifid.data ?? []} onChange={(v) => set('mifidClassification', v ?? '')} />

              {!form.byUniqueId && (
                <>
                  <div className="col-lg-4">
                    <label className="form-label">Group</label>
                    <Select2Async placeholder="All" value={form.group || null} fetch={filterDataPaged.groups} onChange={(v) => set('group', v ?? '')} />
                  </div>
                  <Select2 key="accCurrency" className="col-lg-4" label="Acc Currency" allowClear placeholder="All" value={form.accountCurrency} options={currencyOpts} onChange={(v) => set('accountCurrency', v ?? '')} />
                  <div className="col-lg-4">
                    <label className="form-label">Login</label>
                    <input className="form-control" inputMode="numeric" value={form.login} onChange={(e) => set('login', e.target.value.replace(/\D/g, ''))} />
                  </div>
                  <Select2 key="connector" className="col-lg-4" label="Connector" allowClear placeholder="All" value={form.connector} options={connectors.data ?? []} onChange={(v) => set('connector', v ?? '')} />
                </>
              )}
              {form.byUniqueId && (
                <div className="col-lg-4">
                  <label className="form-label">Unique Id</label>
                  <input className="form-control" value={form.uniqueId} onChange={(e) => set('uniqueId', e.target.value)} />
                </div>
              )}

              <div className="col-lg-4">
                <label className="form-label">Instruments</label>
                <Select2Multi placeholder="All" value={form.instruments} options={instrumentOpts} disabled={dis.instruments} onChange={(v) => set('instruments', v)} />
              </div>
              <div className="col-lg-4 d-flex align-items-end">
                <Switch label="MAR Instruments" checked={form.marInstruments} onChange={(c) => set('marInstruments', c)} />
              </div>
              <div className="col-lg-4 d-flex align-items-end">
                <Switch label="By Unique Id" checked={form.byUniqueId} onChange={onByUniqueId} />
              </div>
            </div>
          </Section>

          <Section title="Conditions">
            <div className="row g-2 align-items-end mb-2">
              <Select2 key="type" className="col-lg-4" label="Type" searchable={false} allowClear={false} value={form.type} options={typeOpts} onChange={(v) => set('type', v || String(TsAlertProfileType.Deals))} />
            </div>

            <h6 className="fw-semibold border-top pt-2 mt-2">Trade Conditions</h6>
            <div className="row g-2 align-items-end">
              <NumField className="col-lg-3" label="Trade Time" suffix="seconds" value={form.tradeTime} disabled={dis.tradeTime} onChange={(v) => set('tradeTime', v)} />
              <NumField className="col-lg-3" label="Trade Points" suffix="points" value={form.tradePoints} disabled={dis.tradePoints} onChange={(v) => set('tradePoints', v)} />
              <OpAmount className="col-lg-6" label="Volume" op={form.volumeOperator} amount={form.volume} disabled={dis.volume} onOp={(v) => set('volumeOperator', v)} onAmount={(v) => set('volume', v)} />
              <OpAmount className="col-lg-6" label="Notional" op={form.notionalOperator} amount={form.notional} disabled={dis.notional} onOp={(v) => set('notionalOperator', v)} onAmount={(v) => set('notional', v)} />
              <Select2 key="session" className="col-lg-3" label="Trading Session" searchable={false} allowClear={false} value={form.tradeSession} options={SESSION_OPTS} onChange={onSession} disabled={dis.session} />
              {sessionTimesVisible && (
                <>
                  <div className="col-lg-3">
                    <label className="form-label">{timeLabel('open')}</label>
                    <TimePicker value={form.openTimeRaw} onChange={(v) => set('openTimeRaw', v)} />
                  </div>
                  <div className="col-lg-3">
                    <label className="form-label">{timeLabel('close')}</label>
                    <TimePicker value={form.closeTimeRaw} onChange={(v) => set('closeTimeRaw', v)} />
                  </div>
                </>
              )}
            </div>

            <h6 className="fw-semibold border-top pt-2 mt-3">Profit Thresholds</h6>
            <div className="row g-2 align-items-center">
              <div className="col-lg-3">
                <Switch label="P/L Threshold (per trade)" checked={form.profitThresholdsPerTrade} disabled={dis.perTrade} onChange={onPerTrade} />
              </div>
              {form.profitThresholdsPerTrade && !dis.perTrade && (
                <NumField className="col-lg-3" label="Amount" allowNegative value={form.profitThresholdsAmountPerTrade} onChange={(v) => set('profitThresholdsAmountPerTrade', v)} />
              )}
              <div className="col-lg-3">
                <Switch label="Cumulative P/L Threshold" checked={form.cumulativeProfitThresholds} disabled={dis.cumulative} onChange={onCumulative} />
              </div>
              {form.cumulativeProfitThresholds && !dis.cumulative && (
                <>
                  <NumField className="col-lg-3" label="Amount" allowNegative value={form.cumulativeProfitThresholdsAmount} onChange={(v) => set('cumulativeProfitThresholdsAmount', v)} />
                  <div className="col-lg-3">
                    <Switch label="Include Unrealized P/L" checked={form.cumulativeProfitThresholdsFloating} onChange={(c) => set('cumulativeProfitThresholdsFloating', c)} />
                  </div>
                </>
              )}
            </div>

            <h6 className="fw-semibold border-top pt-2 mt-3">Account Conditions</h6>
            <div className="row g-2 align-items-end">
              <OpAmount className="col-lg-6" label="Equity" op={form.equityOperator} amount={form.equity} disabled={dis.equity} onOp={(v) => set('equityOperator', v)} onAmount={(v) => set('equity', v)} />
              <OpAmount className="col-lg-6" label="Margin Level %" op={form.marginLevelOperator} amount={form.marginLevel} disabled={dis.marginLevel} onOp={(v) => set('marginLevelOperator', v)} onAmount={(v) => set('marginLevel', v)} />
            </div>

            <h6 className="fw-semibold border-top pt-2 mt-3">Overnight (Open Positions)</h6>
            <div className="row g-2 align-items-end">
              <NumField className="col-lg-3" label="Overnight Days" suffix="days" value={form.overnightDays} disabled={dis.overnightDays} onChange={(v) => set('overnightDays', v)} />
            </div>
          </Section>
        </div>

        {/* Right: template loader + profile lists */}
        <div className="col-lg-3">
          <TemplateLoaderList items={templates.data ?? []} loading={templates.isLoading} onUse={useTemplate} />
          <ProfileList
            title="Active Alert Profiles"
            items={active.data?.items ?? []}
            loading={active.isLoading}
            variant="active"
            defaultOpen
            onView={setViewId}
            onDelete={setDeleteTarget}
            onToggle={setToggleTarget}
          />
          <ProfileList
            title="Deleted Alert Profiles"
            items={deleted.data?.items ?? []}
            loading={deleted.isLoading}
            variant="deleted"
            defaultOpen={false}
            onView={setViewId}
          />
        </div>
      </div>

      <ViewProfileModal id={viewId} onClose={() => setViewId(null)} />

      <ConfirmDialog
        show={toggleTarget !== null}
        title="Warning!"
        message={toggleTarget ? (toggleTarget.enabled ? 'Disable this profile?' : 'Enable this profile?') : ''}
        confirmLabel="Yes"
        confirmVariant="primary"
        busy={setEnabledMut.isPending}
        onConfirm={confirmToggle}
        onCancel={() => setToggleTarget(null)}
      />
      <ConfirmDialog
        show={deleteTarget !== null}
        title="Warning!"
        message={`Are you sure you want to delete "${deleteTarget?.profileTag ?? ''}" Profile ?`}
        confirmLabel="Delete"
        busy={deleteMut.isPending}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}

function Section({ title, defaultOpen = true, children }: { title: string; defaultOpen?: boolean; children: ReactNode }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="card custom-card mb-2">
      <div
        className={`card-header p-2 d-flex align-items-center justify-content-between${open ? ' fk-accordion-open' : ''}`}
        role="button"
        onClick={() => setOpen((o) => !o)}
      >
        <div className="card-title mb-0">{title}</div>
        <i className={`fe ${open ? 'fe-chevron-up' : 'fe-chevron-down'}`} />
      </div>
      {open && <div className="card-body">{children}</div>}
    </div>
  );
}

function NumField({ className, label, suffix, value, onChange, disabled, allowNegative }: { className?: string; label: string; suffix?: string; value: string; onChange: (v: string) => void; disabled?: boolean; allowNegative?: boolean }) {
  return (
    <div className={className}>
      <label className="form-label">{label}</label>
      {suffix ? (
        <div className="input-group">
          <input type="number" className="form-control" value={value} disabled={disabled} min={allowNegative ? undefined : 0} onChange={(e) => onChange(e.target.value)} />
          <span className="input-group-text">{suffix}</span>
        </div>
      ) : (
        <input type="number" className="form-control" value={value} disabled={disabled} min={allowNegative ? undefined : 0} onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  );
}

function OpAmount({ className, label, op, amount, disabled, onOp, onAmount }: { className?: string; label: string; op: string; amount: string; disabled?: boolean; onOp: (v: string) => void; onAmount: (v: string) => void }) {
  const showAmount = Number(op) !== 0 && !disabled;
  return (
    <div className={`${className ?? ''} d-flex align-items-end gap-1`}>
      <div style={{ flex: '1 1 0' }}>
        <label className="form-label">{label}</label>
        <Select2 searchable={false} allowClear={false} value={op} options={OP_OPTS} onChange={(v) => onOp(v || '0')} disabled={disabled} />
      </div>
      {showAmount && (
        <div style={{ flex: '1 1 0' }}>
          <input type="number" className="form-control" placeholder="Amount" value={amount} onChange={(e) => onAmount(e.target.value)} />
        </div>
      )}
    </div>
  );
}
